import {
  getLanguageInfo,
  isSupportedLanguage,
  isLevel,
} from "@lingoshade/domain";
import { MoreVert } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store";
import MaterialFilters from "../../texts/components/MaterialFilters";
import {
  changeStudentAssignment,
  closeStudentAssignments,
  fetchStudentAssignments,
} from "../../texts/store/materialsSlice";

import type { TextListItem } from "@lingoshade/api";
import type {
  StudentAssignment,
  StudentRelationship,
} from "@lingoshade/domain";

interface Props {
  entry: StudentRelationship;
  canTeach: boolean;
}
export default function StudentAssignmentsPanel({
  entry,
  canTeach,
}: Props): React.ReactElement {
  const { t } = useTranslation("texts");
  const { t: common } = useTranslation("common");
  const dispatch = useAppDispatch();
  const data = useAppSelector((state) => state.materials.studentAssignments);
  const [filters, setFilters] = useState({
    language: isSupportedLanguage(entry.student?.targetLanguage)
      ? entry.student.targetLanguage
      : "",
    level: isLevel(entry.student?.level) ? entry.student.level : "",
  });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<TextListItem | null>(null);
  const [page, setPage] = useState(0);
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    assignment: StudentAssignment;
  } | null>(null);
  const [revoking, setRevoking] = useState<StudentAssignment | null>(null);
  const query = {
    language: filters.language || undefined,
    level: filters.level || undefined,
    search: search.length >= 3 ? search : undefined,
  };
  useEffect((): (() => void) => {
    const timer = window.setTimeout((): void => {
      void dispatch(
        fetchStudentAssignments({
          id: entry.id,
          filters: {
            language: filters.language || undefined,
            level: filters.level || undefined,
            search: search.length >= 3 ? search : undefined,
          },
        }),
      );
    }, 250);
    return (): void => window.clearTimeout(timer);
  }, [dispatch, entry.id, filters.language, filters.level, search]);
  useEffect(
    (): (() => void) => (): void => {
      dispatch(closeStudentAssignments());
    },
    [dispatch],
  );
  const ready = data.id === entry.id;
  const assignments = ready ? data.assignments : [];
  const assignedIds = new Set(assignments.map((item): string => item.textId));
  const loading = !ready || data.loading;
  const busy = ready && data.busy;
  const options = ready
    ? data.catalog.filter((item): boolean => !assignedIds.has(item.id))
    : [];
  const describe = (language: string, level: string): string =>
    `${isSupportedLanguage(language) ? getLanguageInfo(language).nativeName : language} · ${common(`levels.${level}`)}`;
  const send = async (): Promise<void> => {
    if (!selected) return;
    try {
      await dispatch(
        changeStudentAssignment({
          id: entry.id,
          textId: selected.id,
          filters: query,
        }),
      ).unwrap();
      setSelected(null);
      setSearch("");
      setPage(0);
    } catch {
      /* Redux displays the error. */
    }
  };
  const revoke = async (): Promise<void> => {
    if (!revoking) return;
    try {
      await dispatch(
        changeStudentAssignment({
          id: entry.id,
          textId: revoking.textId,
          invitationId: revoking.id,
          filters: query,
        }),
      ).unwrap();
      setRevoking(null);
      setPage(0);
    } catch {
      /* Redux displays the error. */
    }
  };
  return (
    <Box
      sx={{
        gridColumn: "1 / -1",
        borderTop: 1,
        borderColor: "divider",
        pt: 2,
        mt: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t("students.assignments.title")}
      </Typography>
      {ready && data.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(`materials.errors.${data.error}`, {
            defaultValue: t("students.errors.STUDENT_TECHNICAL_ERROR"),
          })}
        </Alert>
      )}
      <Stack spacing={1}>
        <MaterialFilters
          {...filters}
          disabled={busy}
          onChange={(value): void => {
            setFilters(value);
            setSelected(null);
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Autocomplete
            options={options}
            value={selected}
            filterOptions={(items): TextListItem[] => items}
            loading={loading}
            disabled={busy || !canTeach}
            sx={{ flex: 1 }}
            isOptionEqualToValue={(option, value): boolean =>
              option.id === value.id
            }
            getOptionLabel={(option): string =>
              option.topic || t("students.assignments.untitled")
            }
            onChange={(_, value): void => setSelected(value)}
            onInputChange={(_, value, reason): void => {
              if (reason === "input" || reason === "clear") {
                setSearch(value);
                if (reason === "input") setSelected(null);
              }
            }}
            renderOption={(props, option): React.ReactElement => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={key} {...rest}>
                  <Box>
                    <Typography variant="body2">
                      {option.topic || t("students.assignments.untitled")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describe(option.language, option.level)}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params): React.ReactElement => (
              <TextField
                {...params}
                size="small"
                label={t("students.assignments.chooseMaterial")}
              />
            )}
          />
          <Button
            variant="contained"
            disabled={!selected || loading || busy || !canTeach}
            onClick={(): void => {
              void send();
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("students.assign")}
          </Button>
        </Stack>
        {ready && data.hasMore && (
          <Typography variant="caption" color="text.secondary">
            {t("students.assignments.catalogHint")}
          </Typography>
        )}
      </Stack>
      {loading && <LinearProgress sx={{ mt: 2 }} />}
      {!loading && assignments.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t("students.assignments.empty")}
        </Typography>
      )}
      <Box component="ul" sx={{ p: 0, m: 0, mt: 1, listStyle: "none" }}>
        {assignments.slice(page * 10, page * 10 + 10).map(
          (item): React.ReactElement => (
            <Stack
              component="li"
              key={item.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {item.title || t("students.assignments.untitled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {describe(item.language, item.level)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                disabled={busy}
                aria-label={t("students.assignments.actions", {
                  title: item.title || t("students.assignments.untitled"),
                })}
                aria-haspopup="menu"
                onClick={(event): void =>
                  setMenu({ anchor: event.currentTarget, assignment: item })
                }
              >
                <MoreVert />
              </IconButton>
            </Stack>
          ),
        )}
      </Box>
      {assignments.length > 10 && (
        <TablePagination
          component="div"
          count={assignments.length}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
          page={page}
          onPageChange={(_, value): void => setPage(value)}
        />
      )}
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={(): void => setMenu(null)}
      >
        <MenuItem
          component={Link}
          to={`/practice/${menu?.assignment.textId || ""}`}
          onClick={(): void => setMenu(null)}
        >
          {t("materials.open")}
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            if (menu) setRevoking(menu.assignment);
            setMenu(null);
          }}
        >
          {t("students.assignments.revoke")}
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(revoking)}
        onClose={busy ? undefined : (): void => setRevoking(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("students.assignments.confirmRevoke", {
            title: revoking?.title || t("students.assignments.untitled"),
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("students.assignments.revokeConsequences")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={(): void => setRevoking(null)}>
            {t("generateDialog.cancel")}
          </Button>
          <Button
            color="error"
            disabled={busy}
            onClick={(): void => {
              void revoke();
            }}
          >
            {t("students.assignments.revoke")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
